import { request } from "http";
import moment from "moment";
import { useContext, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AuthContext } from "../context/auth.context";

const fetchWithTimeout = async (
  input: RequestInfo | URL,
  init?: RequestInit | undefined
): Promise<Response> => {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), 2000);

  const response = await fetch(input, {
    ...init,
    signal: controller.signal,
  });

  clearTimeout(id);
  return response;
};

const API_BASE_URL =
  process.env.REACT_APP_ENV === "production"
    ? "https://itpiapi.lancaster.ac.uk/occupancy"
    : "https://jedha.lancaster.ac.uk/deskoccupancyapi";

const ORGANISATION_ID = "00000000-0000-0000-0000-000000000000";

export interface SpaceImage {
  id: string;
  authorName: string;
  authorUsername: string;
  submittedTimeUTC: string;
  url: string;
  imageType: number;
}

export interface SpaceTag {
  key: string;
  value: string;
}

export interface UserGroup {
  ldapName: string;
  adName: string;
}

export interface LocationViewModel {
  key: string;
  poiId: number;
  organisationKey: string;
  buildingId: string;
  buildingName?: any;
  name: string;
  totalNumberOfDesks: number;
  availableDesks: number;
  occupancyStatusColor: string;
  tags: string[];
  bookableRoom: boolean;
  spaceNumber: string;
  locationType: number;
  capacity: number;
  radiusSize?: any;
  imageUrl?: any;
  temperature?: any;
  bookedEntitys?: any;
  searchType: number;
  images: SpaceImage[];
  locationPreferences: LocationPreferences;
}

export interface Booking {
  id: string;
  deskId: string;
  locationId: string;
  locationPoiId: number;
  userId: string;
  startDate: Date;
  endDate: Date;
  location: string;
  deskNumber: number;
  description: string;
  locationViewModel: LocationViewModel;
}

export interface UserBookings {
  username: string;
  email: string;
  name: string;
  organisationKey: string;
  subscriptionId: string;
  verified: boolean;
  bookings: Booking[];
}

export interface TimeSlot {
  from: string;
  to: string;
  allAvailableTime: boolean;
  padding: number;
  bookee: string;
}

export interface BookedEntity {
  id: string;
  name: string;
  description: string;
  number: number;
  disabled: boolean;
  timeslots: TimeSlot[];
  tags: string[];
}

export interface Day {
  day: number;
  from: string;
  to: string;
  closed: boolean;
}

export interface UserGroup {
  ldapName: string;
  adName: string;
}

export enum TimeInterval {
  NONE,
  ONE_HOUR,
  FIFTEEN_MINUTES,
  THIRTY_MINUTES,
}

export interface LocationPreferences {
  timePreference: { timeInterval: TimeInterval };
  openingHours: { days: Day[]; overrides: any[] };
  maxDuration: { minutes: number };
  minDuration: { minutes: number };
  defaultDuration: { minutes: number };
  totalDurationPerPeriod: {
    minutes: number;
    period: number;
  };
  bookingWindowLimit: { days: number };
  multipleBookingsAllowed: boolean;
  paddingPreference: { padding: number };
  cancellationWindowPreference: { cancellationPeriod: number };
  frequencyLimitPreference: {
    limit: number;
    period: number;
  };
  backToBackBookingPreference: { hoursBetweenBookings: number };
  bookingLeadTimePreference: {
    bookingLeadTime: number;
  };
  advancedBookingPreference: {
    hoursBookableInAdvance: number;
  };
  allowedUserGroupsPreference: {
    userGroups: UserGroup[];
  };
  checkInEnabled: boolean;
  sendCheckInReminders: boolean;
  usersCanTag: boolean;
  digitalToursUrl?: any;
}

export interface BookingLocation {
  key: string;
  poiId: number;
  active?: boolean;
  organisationKey: string;
  buildingId: string | null;
  buildingName: string;
  name: string;
  totalNumberOfDesks: number;
  availableDesks: number;
  occupancyStatusColor: string;
  tags: string[];
  bookableRoom: boolean;
  spaceNumber?: any;
  locationType: number;
  capacity: number;
  radiusSize: string;
  imageUrl?: any;
  temperature?: any;
  bookedEntitys: BookedEntity[];
  searchType: number;
  longitude: number | null;
  latitude: number | null;
  images: any[];
  locationPreferences: LocationPreferences;
}

export type BookingRequest = {
  privateBooking: boolean;
  bookings: { deskId: string; email: string }[];
  timeslot: { from: string; to: string };
};

export enum RoomType {
  "None" = 0,
  "Desk" = 1,
  "Meeting Room" = 2,
  "Office" = 3,
  "Study Space" = 4,
}

export type CheckInState =
  | "UNKNOWN"
  | "NOT_BOOKED_BUT_AVAILABLE"
  | "NOT_BOOKED_AND_UNAVAILABLE"
  | "CHECKED_IN"
  | "LOADING";

export interface CheckInStatus {
  checkInStatus: CheckInState;
  deskId: string;
  locationId: string;
}

export interface Building {
  key: string;
  organisationKey: string;
  name: string;
  description: string;
}

export const useBookingLocation = (plannonId?: string | number) => {
  const { getToken } = useContext(AuthContext);

  return useQuery(
    ["GetDeskData", plannonId],
    async () => {
      const today = moment();
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/api/organisations/${ORGANISATION_ID}/locations/${plannonId}?from=${today.toISOString()}&to=${today
          .add(7, "days")
          .toISOString()}&searchType=2`,
        { headers: { Authorization: `Bearer ${await getToken()}` } }
      );
      if (!response.ok)
        throw new Error(
          "Something went wrong when getting this location's data."
        );
      const data: BookingLocation = await response.json();
      return data;
    },
    { enabled: plannonId !== undefined, retry: false }
  );
};

export const useBookingUser = () => {
  const { getToken, userDetails } = useContext(AuthContext);

  return useQuery(["GetBookingUser", userDetails.email], async () => {
    const response = await fetch(
      `${API_BASE_URL}/api/users/${userDetails.email}`,
      {
        headers: {
          Authorization: `Bearer ${await getToken()}`,
        },
      }
    );
    if (!response.ok)
      throw new Error("Something went wrong when getting user data.");
    if (response.status === 204) return null;
    const data: any = await response.json();
    return data;
  });
};

export const useRegisterBookingUser = () => {
  const { getToken, userDetails } = useContext(AuthContext);
  const queryClient = useQueryClient();

  return useMutation(
    ["RegisterUser", userDetails.email],
    async () => {
      // console.log("STARTING");
      const response = await fetch(`${API_BASE_URL}/api/users/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${await getToken()}`,
        },
        body: JSON.stringify({
          username: userDetails.email,
          email: userDetails.email,
          name: userDetails.given_name + " " + userDetails.family_name,
          organisationKey: ORGANISATION_ID,
        }),
      });
      // console.log("ENDING", response.ok);
      if (!response.ok) {
        console.log("error occurred");
        throw new Error("Something went wrong when registering.");
      } else {
        console.log("User registered successfully.");
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["GetBookingUser", userDetails.email]);
      },
    }
  );
};

export const useValidateBooking = (bookingRequest?: BookingRequest) => {
  const { getToken, userDetails } = useContext(AuthContext);
  const booking = bookingRequest?.bookings?.[0];

  return useQuery(
    ["ValidateBooking", bookingRequest],
    async () => {
      const response = await fetch(
        `${API_BASE_URL}/api/desks/verify-bookable/${booking?.deskId}?username=${userDetails.preferred_username}&from=${bookingRequest?.timeslot.from}&to=${bookingRequest?.timeslot.to}`,
        { headers: { Authorization: `Bearer ${await getToken()}` } }
      );
      const data = JSON.parse(await response.text());
      if (!response.ok) throw new Error(data?.messages?.join(". "));

      return data;
    },
    { enabled: bookingRequest !== undefined, retry: false }
  );
};

export const useBookDeskMutation = (
  plannonId?: number | string,
  bookingRequest?: BookingRequest
) => {
  const { getToken, userDetails } = useContext(AuthContext);
  const queryClient = useQueryClient();

  return useMutation(
    async () => {
      if (bookingRequest) {
        const { privateBooking, ...request } = bookingRequest;

        const response = await fetch(
          `${API_BASE_URL}/api/locations/${plannonId}/desks/book/${userDetails.preferred_username}` +
            (privateBooking ? "?hidden=true" : ""),
          {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${await getToken()}`,
              Accept: "*/*",
              "Content-Type": "application/json",
            },
            body: JSON.stringify(request),
          }
        );

        const data = await response.json();

        if (!response.ok) {
          const msg = data?.messages ? data.messages?.[0] : null;
          throw new Error(
            msg
              ? msg
              : "An error occured while booking this desk, please try again later and if it still fails, contact support."
          );
        }
        return response;
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          "GetBookings",
          userDetails.preferred_username,
        ]);
      },
    }
  );
};

export const useBookingSearch = (
  deskType?: RoomType,
  date?: string,
  buildingId?: string
) => {
  const { getToken } = useContext(AuthContext);

  return useQuery<BookingLocation[]>(
    ["BookingSearch", deskType, date, buildingId],
    async () => {
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/api/organisations/${ORGANISATION_ID}/locations?locationType=${deskType}&searchType=2&active=true` +
          (date
            ? `&from=${date}&to=${moment(date).add(1, "hour").toISOString()}`
            : "") +
          (buildingId
            ? `&buildingId=${buildingId}&from=${moment().toISOString()}&to=${moment()
                .add(1, "hour")
                .toISOString()}`
            : ""),
        { headers: { Authorization: `Bearer ${await getToken()}` } }
      );

      if (!response.ok)
        throw new Error("Failed to search for available bookings");
      const responseJson = await response.json();
      return responseJson;
    },
    { enabled: !!deskType && (!!date || !!buildingId) }
  );
};

export const useBookings = () => {
  const { getToken, userDetails } = useContext(AuthContext);

  return useQuery<UserBookings>(
    ["GetBookings", userDetails.preferred_username],
    async () => {
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/api/users/${userDetails.preferred_username}`,
        { headers: { Authorization: `Bearer ${await getToken()}` } }
      );

      if (!response.ok)
        throw new Error(
          "Something went wrong when finding your bookings. Please try again later."
        );
      if (response.status === 204) {
        throw new Error("No bookings found.");
      }

      return response.json();
    }
  );
};

export const useBooking = (bookingId?: string) => {
  const bookings = useBookings();

  const booking = useMemo(() => {
    return bookings.data?.bookings.find((b) => b.id === bookingId);
  }, [bookings, bookingId]);

  return { ...bookings, data: booking };
};

export const useCancelBookingMutation = (
  deskId?: string,
  bookingId?: string
) => {
  const { getToken, userDetails } = useContext(AuthContext);
  const queryClient = useQueryClient();

  return useMutation(
    async () => {
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/api/desks/${deskId}/bookings/${userDetails.preferred_username}/${bookingId}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${await getToken()}` },
        }
      );

      if (!response.ok) throw new Error("Failed to cancel booking");

      return response;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          "GetBookings",
          userDetails.preferred_username,
        ]);
      },
    }
  );
};

export const useEndBookingMutation = (deskId?: string, bookingId?: string) => {
  const { getToken, userDetails } = useContext(AuthContext);
  const queryClient = useQueryClient();

  return useMutation(
    async () => {
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/api/desks/${deskId}/bookings/${userDetails.preferred_username}/${bookingId}/end`,
        {
          method: "PUT",
          headers: { Authorization: `Bearer ${await getToken()}` },
        }
      );

      if (!response.ok) throw new Error("Failed to end booking");

      return response;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          "GetBookings",
          userDetails.preferred_username,
        ]);
      },
    }
  );
};

export const useCheckIn = (deskId?: string) => {
  const { getToken } = useContext(AuthContext);

  return useQuery<CheckInStatus>(
    ["CheckIn", deskId],
    async () => {
      const response = await fetch(
        `${API_BASE_URL}/api/desks/${deskId}/checkin`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${await getToken()}`,
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) throw new Error("Failed to check in to this desk");

      const data = await response.json();

      if (data.checkInStatus === "NOT_BOOKED_AND_UNAVAILABLE") {
        throw new Error(
          "Sorry, this desk is currently booked by someone else, would you like to book this desk for later or find a different one?"
        );
      }

      return data;
    },
    { enabled: !!deskId, cacheTime: 0, retry: false }
  );
};

export const useAvailableTags = (plannonId?: string) => {
  const { getToken } = useContext(AuthContext);

  return useQuery<SpaceTag[]>(
    ["GetTags", plannonId],
    async () => {
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/deskoccupancyapi/api/locations/${plannonId}/get-available-tags`,
        {
          headers: {
            Authorization: `Bearer ${await getToken()}`,
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }
      );
      if (!response.ok)
        throw new Error(
          "Something went wrong getting the available tags for this space."
        );
      return response.json();
    },
    { enabled: !!plannonId }
  );
};

export const useSuggestTagMutation = (plannonId?: string) => {
  const { getToken } = useContext(AuthContext);
  const queryClient = useQueryClient();

  return useMutation(
    async (tagId: string) => {
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/api/locations/${plannonId}/suggest-tags`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${await getToken()}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({ tagIds: [tagId] }),
        }
      );

      if (!response.ok) throw new Error("Failed to suggest tag");

      return response;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["GetTags", plannonId]);
      },
    }
  );
};

export const useUploadMutation = (plannonId?: string) => {
  const { getToken, userDetails } = useContext(AuthContext);
  const queryClient = useQueryClient();

  return useMutation(
    async (files: FileList) => {
      const results = await Promise.allSettled(
        Array.from(files).map(async (file) => {
          const formData = new FormData();
          formData.append("file", file);

          const response = await fetchWithTimeout(
            `${API_BASE_URL}/api/organisations/${ORGANISATION_ID}/locations/${plannonId}/images`,
            {
              method: "POST",
              headers: {
                Authorization: `Bearer ${await getToken()}`,
                Accept: "*/*",
              },
              body: formData,
            }
          );

          if (!response.ok) throw new Error("Failed to upload file");

          return response;
        })
      );

      const failures = results
        .map((r) => (r.status === "rejected" ? 1 : 0) as number)
        .reduce((prev, curr) => prev + curr);

      if (failures > 0) {
        throw new Error(`Failed to upload ${failures} files`);
      }

      return true;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          "GetBookings",
          userDetails.preferred_username,
        ]);
      },
    }
  );
};

export const useReportFaultMutation = (bookingId?: string) => {
  const { getToken } = useContext(AuthContext);

  return useMutation(async (message: string) => {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/bookings/${bookingId}/report-fault`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${await getToken()}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ message }),
      }
    );

    if (!response.ok)
      throw new Error("There was an error reporting your issue.");

    return response;
  });
};

export const useBuildings = () => {
  const { getToken } = useContext(AuthContext);

  return useQuery<Building[]>(["GetBuildings"], async () => {
    const response = await fetch(
      `${API_BASE_URL}/api/organisations/${ORGANISATION_ID}/buildings?activeOnly=true`,
      {
        headers: {
          Authorization: `Bearer ${await getToken()}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok)
      throw new Error(
        "Something went wrong getting the buildings on campus. Please try again later."
      );

    return response.json();
  });
};
